import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Mic, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useCharles } from "@/contexts/CharlesContext";

/**
 * Persistent floating Charles orb.
 * - Always visible on employee pages (mounted in EmployeeLayout).
 * - Hidden on the dedicated /app/employee/charles page (it has its own full UI).
 * - One ElevenLabs session, shared across navigation via CharlesContext.
 */
export function CharlesOrb() {
  const location = useLocation();
  const onDedicatedPage = location.pathname.startsWith("/app/employee/charles");

  const { session, conversation, configError, started, voiceOptIn, forceTextOnly, start, stop, sendText } =
    useCharles();

  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  if (onDedicatedPage) return null;
  if (session.status === "error" || configError) return null;

  const isReady = session.status === "ready";
  const isConnected = conversation.status === "connected";
  const isSpeaking = conversation.isSpeaking;
  const showVoiceUI = voiceOptIn && !forceTextOnly;

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3 md:bottom-6 bottom-24">
      {open && (
        <div
          ref={panelRef}
          className="w-80 rounded-2xl border border-border bg-card shadow-2xl p-4 space-y-3 animate-in fade-in slide-in-from-bottom-2"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Charles</p>
              <p className="text-xs text-muted-foreground">
                {isConnected
                  ? isSpeaking
                    ? "Speaking…"
                    : "Listening"
                  : started
                    ? "Connecting…"
                    : "Tap start to talk"}
              </p>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {!started ? (
            <Button
              onClick={start}
              disabled={!isReady}
              className="w-full"
              size="sm"
            >
              {!isReady ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                  Preparing…
                </>
              ) : showVoiceUI ? (
                <>
                  <Mic className="h-3.5 w-3.5 mr-2" />
                  Start voice
                </>
              ) : (
                "Start chat"
              )}
            </Button>
          ) : (
            <div className="space-y-2">
              {!showVoiceUI && (
                <div className="flex gap-2">
                  <Input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Say what's on your mind…"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && text.trim()) {
                        sendText(text);
                        setText("");
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    onClick={() => {
                      sendText(text);
                      setText("");
                    }}
                  >
                    Send
                  </Button>
                </div>
              )}
              <Button variant="outline" size="sm" className="w-full" onClick={stop}>
                End conversation
              </Button>
            </div>
          )}
        </div>
      )}

      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Talk to Charles"
        className={cn(
          "relative h-14 w-14 rounded-full transition-transform hover:scale-105 active:scale-95",
          "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background",
          isConnected ? "focus:ring-emerald-400" : "focus:ring-orange-400",
          isConnected && "animate-pulse",
        )}
        style={{
          // Orange base — always rendered
          background:
            "radial-gradient(circle at 30% 30%, #ffd6a8 0%, #ff8a3d 35%, #e8551a 75%, #a8350a 100%)",
          boxShadow: isConnected
            ? "0 0 32px 6px rgba(52, 211, 153, 0.6), inset -4px -6px 12px rgba(0,0,0,0.25)"
            : "0 8px 20px rgba(232, 85, 26, 0.35), inset -4px -6px 12px rgba(0,0,0,0.25)",
          transition: "box-shadow 700ms ease",
        }}
      >
        {/* Green sphere layer, fades in over the orange */}
        <span
          aria-hidden
          className="absolute inset-0 rounded-full"
          style={{
            background:
              "radial-gradient(circle at 30% 30%, #bbf7d0 0%, #34d399 35%, #10b981 75%, #065f46 100%)",
            opacity: isConnected ? 1 : 0,
            transition: "opacity 700ms ease",
          }}
        />
        {/* Speaking pulse */}
        {isSpeaking && (
          <span
            className="absolute inset-0 rounded-full animate-ping"
            style={{
              background: isConnected
                ? "rgba(52, 211, 153, 0.5)"
                : "rgba(255, 138, 61, 0.45)",
            }}
          />
        )}
        {/* Idle breathing ring when connected but quiet */}
        {isConnected && !isSpeaking && (
          <span
            className="absolute -inset-1 rounded-full opacity-70"
            style={{
              boxShadow: "0 0 0 2px rgba(52, 211, 153, 0.45)",
              animation: "pulse 2.4s ease-in-out infinite",
            }}
          />
        )}
        {/* Highlight */}
        <span
          className="absolute top-2 left-3 h-3 w-3 rounded-full opacity-80"
          style={{ background: "rgba(255,255,255,0.75)", filter: "blur(1px)" }}
        />
      </button>
    </div>
  );
}
